import React from 'react';
import Header from './Header.js';
import Leaf from './Leaf.js';
import Flower from './Flower.js';
import pot from './pot.svg';
import flowerSvg from './flower.svg';
import leafSvg from './Leaf.svg';
import { gsap } from 'gsap';
import { updatePageColors } from './helpers/Helpers.js';
import './Projects.css';

// Preload images to prevent stuttering on first animation
const preloadImages = (sources) => {
  return Promise.all(
    sources.map((src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve; // resolve even on error to not block
        img.src = src;
      });
    })
  );
};

// Generate evenly spaced positions for leaves and flowers
const generatePositions = (count, startOffset, spacing) => {
  return Array.from({ length: count }, (_, i) => startOffset + i * spacing);
};

/*
TODOs:
  - add project info popup
  - add page transition
*/
class Projects extends React.Component {
  constructor(props) {
    super(props);
    
    // Project data - add your projects here and positions will be auto-generated
    this.projectNames = ["Shaker", "Values Finder", "Flight", "Leftovers", "Stability"];
    this.projectLinks = [
      "https://apps.apple.com/us/app/shaker-cocktail-manager/id6756530261",
      "https://valuesselector.netlify.app/",
      "https://flight-orb.netlify.app/",
      "https://leftovers-orb.netlify.app/",
      "https://github.com/b-sheppard/Stability"
    ];
    
    // Dynamic position generation based on project count
    const projectCount = this.projectNames.length;
    const flowerSpacing = 500; // space between flowers
    const leafSpacing = 500;   // space between leaves
    
    // Generate positions: flowers appear at each project, leaves fill gaps between
    this.inactiveFlowers = generatePositions(projectCount, 450, flowerSpacing);
    this.inactiveLeafs = generatePositions(projectCount + 1, 250, leafSpacing);
    
    // Calculate max height based on last flower position + buffer
    this.maxPlantHeight = this.inactiveFlowers[this.inactiveFlowers.length - 1] + 400;
    
    this.state = {
      plantHeight: 10,
      activeLeafs: [],
      activeFlowers: [],
      deletingLeaf: false,
      deletingFlower: false,
      imagesLoaded: false,
    };
    
    this.grow = this.grow.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    // Ensure colors are set after component mounts (backup to route-based update)
    updatePageColors('/projects');
    
    // Preload images, but enable interaction after timeout as fallback
    const enableInteraction = () => {
      if (!this.state.imagesLoaded) {
        this.setState({ imagesLoaded: true });
      }
    };
    
    // Timeout fallback - enable interaction after 1 second regardless
    setTimeout(enableInteraction, 1000);
    
    // Also enable when images actually load (might be faster)
    preloadImages([flowerSvg, leafSvg]).then(enableInteraction);
    
    window.addEventListener('wheel', this.handleScroll);
    var curY = 0;
    window.addEventListener('touchstart', function (e) {
       curY = e.touches[0].clientY;
    });
    window.addEventListener('touchmove', (e) => {
      this.grow(-0.75*(e.touches[0].clientY - curY));
      curY = e.touches[0].clientY;
    });
  }

  componentWillUnmount() {
    window.removeEventListener('wheel', this.handleScroll);
  }

  handleScroll(event) {
    let scrollMultiplier = 0.15;
    this.grow(event.deltaY * scrollMultiplier);
  }

  grow(newHeight) {
    // Don't allow growth until images are preloaded
    if (!this.state.imagesLoaded) return;
    
    let plantHeight = Math.max(this.state.plantHeight + newHeight, 10);
    if (this.state.plantHeight > this.maxPlantHeight && newHeight > 0) {
      return;
    }
    var activeFlowers = this.state.activeFlowers;
    // add flower
    if (this.inactiveFlowers.length > 0 && this.inactiveFlowers[0] + 200 < plantHeight) {
      let flowerHeight = this.inactiveFlowers.shift();
      activeFlowers.push(<Flower key={flowerHeight}
        yPos={flowerHeight}
        text={this.projectNames[activeFlowers.length]}
        link={this.projectLinks[activeFlowers.length]}/>
      );
    }
    // remove flower
    if (!this.state.deletingFlower && this.state.activeFlowers.length > 0 && parseInt(this.state.activeFlowers[this.state.activeFlowers.length - 1].key) + 200 > plantHeight) {
      this.setState({deletingFlower: true})
      let yPos = parseInt(activeFlowers[activeFlowers.length - 1].key);
      gsap.to(`#Flower-Center-${yPos}`, { scale: 0, duration: 0.4 });
      gsap.to(`#Flower-Petals-${yPos}`, { scale: 0, duration: 0.3, onComplete: () => {
        let flower = activeFlowers.pop();
        this.inactiveFlowers.unshift(parseInt(flower.key));
        this.setState({
          activeFlowers: activeFlowers,
          deletingFlower: false
        })
      }})
    }
    var activeLeafs = this.state.activeLeafs;
    // add leaf
    if (this.inactiveLeafs.length > 0 && this.inactiveLeafs[0] + 50 <= plantHeight) {
      let leafHeight = this.inactiveLeafs.shift();
      activeLeafs.push(<Leaf
        key={leafHeight}
        yPos={leafHeight + 200}
        text={leafHeight}
        reverse={activeLeafs.length % 2}
      />)
    }
    // remove leaf
    if (!this.state.deletingLeaf && this.state.activeLeafs.length > 0 && parseInt(this.state.activeLeafs[this.state.activeLeafs.length - 1].key) + 50 > plantHeight) {
      this.setState({deletingLeaf: true})
      let yPos = parseInt(activeLeafs[activeLeafs.length - 1].key) + 200;
      if (this.state.activeLeafs.length % 2) {
        gsap.to(`#Leaf-Wrapper-${yPos}`, { scale: 0, duration: 0.35, x: -150, rotation: 50, onComplete: () => {
          let leaf = activeLeafs.pop();
          this.inactiveLeafs.unshift(parseInt(leaf.key));
          this.setState({
            activeLeafs: activeLeafs,
            deletingLeaf: false
          });
        }})
      }
      else {
        gsap.to(`#Leaf-Wrapper-${yPos}`, { opacity: 0, scale: 0, duration: 0.35, x: -150, rotation: "315_short", onComplete: () => {
          let leaf = activeLeafs.pop();
          this.inactiveLeafs.unshift(parseInt(leaf.key));
          this.setState({
            activeLeafs: activeLeafs,
            deletingLeaf: false
          });
        }})
      }
    }
    this.setState({
      plantHeight: plantHeight,
      activeLeafs: activeLeafs,
      activeFlowers: activeFlowers
    });
  }

  render() {
    return (
      <div className="Projects-Wrapper">
        <Header selected="Projects" backgroundColor="var(--purple)" textColor="var(--white)"/>
        <div
          className="Plant-Wrapper"
          style={{height: `${350 + this.state.plantHeight}px`}}>
          <div className="Leaf-Container" style={{position: "relative"}}>
            {
              this.state.activeLeafs
            }
          </div>
          <div className="Flower-Container" style={{position: "relative", "zIndex": "5"}}>
            {
              this.state.activeFlowers
            }
          </div>
          <div style={{height: "30px", width: "100vw"}}></div>
          <div style={{position: "relative", display: "inline-block"}}>
            <img
              className="Pot"
              src={pot}
              alt="Pot"/>
            <p className="Scroll-Me-Text">scroll me</p>
          </div>
          <div
            className="Stem"
            style={{height: `${this.state.plantHeight}px`}}>
              <div
                style={
                  {
                    background: "black",
                    opacity: "0.2",
                    height: "100%",
                    width: "12px"
                  }}>
              </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Projects;
